import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import {Organization, User} from "../../../types/userTypes";
import Button from "@mui/material/Button";
import {DataGridPro, GridColDef} from "@mui/x-data-grid-pro";
import React, {useEffect, useState} from "react";
import {APP_PERMISSION, defaultOrganization, EntityType, organizationColumns} from "../../../constants/userConstants";
import {Competition} from "../../../types/eventTypes";
import OrganizationForm from "./OrganizationForm";
import {
    createOrganization,
    deleteOrganization,
    getOrganizations,
    getUsers,
    updateOrganization
} from "../../../queries/userQueries";
import {getCompetitions} from "../../../queries/competitionQueries";
import {useSnackbar} from "notistack";
import {IconButton, Stack} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import PermissionRendering from "@/pages/custom-components/UserComponents/PermissionRendering"; // Tipico hook per enqueueSnackbar, gestisci pure l'import come preferisci
import { circularIconHoverSx } from "@/styles/iconHover";

interface OrganizationTableProps {
    updateEntity: EntityType.users | EntityType.organizations | "";
    handleUpdateEntity: (value: EntityType.users | EntityType.organizations | "") => void;
}

const OrganizationTable: React.FC<OrganizationTableProps> = ({ updateEntity, handleUpdateEntity }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganization, setLoadingOrganization] = useState<boolean>(true);
    const [loadingForm, setLoadingForm] = useState<boolean>(true);
    const [users, setUsers] = useState<User[]>([]);
    const [competitions, setCompetitions] = useState<Competition[]>([]);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formMode, setFormMode] = useState<'create' | 'update'>('create');
    const [selectedOrganization, setSelectedOrganization] = useState<Organization>(JSON.parse(JSON.stringify(defaultOrganization)));
    const [tableColumns, setTableColumns] = useState<GridColDef<Organization>[]>([]);
    const [openDialogDeleteOrganization, setOpenDialogDeleteOrganization] = useState<boolean>(false);

    async function loadOrganizations() {
        try {
            setLoadingOrganization(true);
            const organizations = await getOrganizations();
            const allTeamsMapped = organizations.map(org => ({...org, teams: org.teams.map(team => ({id: team.id, value: team.name}))})

            );
            setOrganizations(allTeamsMapped);
            setLoadingOrganization(false);
        } catch (e) {
            console.error(e);
            setLoadingOrganization(false);
            enqueueSnackbar("Problems retrieving Organizations");
        }
    }

    async function loadUsers() {
        try {
            const users = await getUsers();
            setUsers(users);
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Failed to load users Options for organizations");
        }
    }

    async function loadCompetitions() {
        try {
            const competitions = await getCompetitions();
            setCompetitions(competitions.map(competition => ({...competition, name: competition.name + " - " + competition.season })));
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Failed to load users Options for organizations");
        }
    }

    async function handleOrganizationChange(name: keyof Organization, value: any) {
        setSelectedOrganization(prevState => ({ ...prevState, [name]: value }));
        handleUpdateEntity(EntityType.organizations)
    }

    useEffect(() => {
        loadOrganizations();
        addActionColumns();
    }, []);

    async function loadDropDownData() {
        const promises = [loadCompetitions(), loadUsers()];
        await Promise.all(promises);
        setLoadingForm(false);
    }

    useEffect(() => {
        if (isFormOpen)
            loadDropDownData();
    }, [isFormOpen]);

    useEffect(() => {
        if (updateEntity === EntityType.users || updateEntity === EntityType.organizations) {
            loadOrganizations();
            loadUsers();
            loadCompetitions();
            addActionColumns();
            handleUpdateEntity("");
        }
    }, [updateEntity]);

    useEffect(() => {
        if (selectedOrganization.competitions.length > 0) {
            let id_competitions: string[] = [];
            selectedOrganization.competitions.map((item) => {
                id_competitions.push(item.id);
            });
        }
    }, [selectedOrganization]);

    function handleClose() {
        setIsFormOpen(false);
        setSelectedOrganization(JSON.parse(JSON.stringify(defaultOrganization)));
        setFormMode("create");
    }

    async function onUpdateClick(organization: Organization) {
        organization.competitions = organization.competitions.map(competition => ({...competition, name: competition.name + " - " + competition.season }));
        setSelectedOrganization(organization);
        setIsFormOpen(true);
        setFormMode("update");
    }

    async function handleFormSubmit() {
        try {
            if (formMode === 'create') {
                const tempSelectedOrganization = { ...selectedOrganization };
                const createdOrganizationId: { id: string } = await createOrganization(tempSelectedOrganization);
                tempSelectedOrganization.id = createdOrganizationId.id;
                setOrganizations(prevState => [...prevState, JSON.parse(JSON.stringify(tempSelectedOrganization))]);
            } else if (formMode === 'update') {
                await updateOrganization(selectedOrganization);
                const tempOrgs = [...organizations];
                const updatedOrgIndex = tempOrgs.findIndex(user => user.id === selectedOrganization.id);
                if (updatedOrgIndex > -1) {
                    tempOrgs[updatedOrgIndex] = selectedOrganization;
                    setOrganizations(tempOrgs);
                }
                handleUpdateEntity(EntityType.organizations);
            }

            handleClose();
            enqueueSnackbar(`Organization ${formMode === 'create' ? 'created' : 'updated'}  successfully`);
        } catch (e: any) {
            if (e.response && e.response.data && e.response.data.detail) {
                console.error(e);
                enqueueSnackbar(`Error ${formMode === 'create' ? 'creating' : 'updating'} organization: ${e.response.data.detail.message}`);
            } else {
                console.error(e);
                enqueueSnackbar(`Error ${formMode === 'create' ? 'creating' : 'updating'}  organization`);
            }
        }
    }

    function onDeleteClick(organization: Organization) {
        setSelectedOrganization(organization);
        setOpenDialogDeleteOrganization(true);
    }

    async function handleDeleteOrganization() {
        try {
            await deleteOrganization(selectedOrganization.id);
            setOrganizations(prevState => prevState.filter(org => org.id !== selectedOrganization.id));
            handleUpdateEntity(EntityType.organizations)
            enqueueSnackbar("Organization Deleted Successfully");
            setOpenDialogDeleteOrganization(false);
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Organization Delete Failed");
        }
    }

    function onCreateClick() {
        setIsFormOpen(true);
        setFormMode("create");
    }

    function addActionColumns() {
        const tempColumns = [...organizationColumns];
        tempColumns.push(
            {
                field: "actions",
                headerName: "",
                flex: 1,
                renderCell: (params) => (
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        spacing={1}
                        sx={{ width: '100%', height: '100%' }}
                    >

                        <PermissionRendering permissions={[APP_PERMISSION.UPDATE_ORGANIZATION]}>
                            <IconButton
                                data-tour={"users_organization_edit_icon"}
                                onClick={(e) => onUpdateClick(params.row)}
                                sx={circularIconHoverSx()}
                            >
                                <EditOutlinedIcon />
                            </IconButton>
                        </PermissionRendering>


                        <PermissionRendering permissions={[APP_PERMISSION.DELETE_ORGANIZATION]}>
                            <IconButton
                                data-tour={"users_organization_delete_icon"}
                                onClick={(e) => onDeleteClick(params.row)}
                                sx={circularIconHoverSx()}
                            >
                                <DeleteOutlined />
                            </IconButton>
                        </PermissionRendering>

                    </Stack>
                ),
            },
        );
        setTableColumns(tempColumns);
    }

    return (
        <Box sx={{marginTop: 3}}>
            <Modal
                open={openDialogDeleteOrganization}
                onClose={() => setOpenDialogDeleteOrganization(false)}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1,
                    minWidth: 300
                }}>
                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                        Delete Organization
                    </Typography>
                    <Typography sx={{ mb: 3 }}>
                        {`Are you sure you want to delete ${selectedOrganization.name}?`}
                    </Typography>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button variant="contained" onClick={() => setOpenDialogDeleteOrganization(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={() => handleDeleteOrganization()}>
                            Confirm
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Dialog
                open={isFormOpen}
                onClose={handleClose}
                fullWidth={false}
                sx={{ '& .MuiDialog-paper': { width: '65vw', maxWidth: 'none' } }}
            >
                <DialogTitle>
                    {`${formMode === 'create' ? 'Create Organization' : 'Update Organization'}`}
                </DialogTitle>
                <DialogContent>
                    {loadingForm ? (
                        <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%" minHeight={200}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <OrganizationForm
                            users={users}
                            organization={selectedOrganization}
                            sendFormLabel={formMode === 'create' ? 'Create Organization' : 'Update Organization'}
                            competitions={competitions}
                            onFieldChange={handleOrganizationChange}
                            onSubmit={handleFormSubmit}
                            onCloseClick={handleClose}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <PermissionRendering permissions={[APP_PERMISSION.CREATE_ORGANIZATION]}>
                <Button data-tour={"users_button_create_organization"} variant="contained" startIcon={<AddCircleOutlineOutlinedIcon/>} onClick={onCreateClick}>
                    Create Organization
                </Button>
            </PermissionRendering>

            <DataGridPro
                data-tour={"users_organization_total"}
                rows={organizations}
                columns={tableColumns}
                getRowId={(row) => row.id}
                loading={loadingOrganization}
                pageSizeOptions={[5, 25, 50]}
                columnVisibilityModel={{
                    id: false
                }}
                sx={{
                    marginTop: 1
                }}
            />
        </Box>
    );
};

export default OrganizationTable;